import BpkRating, {
  RATING_SIZES,
} from '@skyscanner/backpack-web/bpk-component-rating';
import { RATING_SCALES } from '@skyscanner/backpack-web/bpk-component-rating/src/common-types';
import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import { withI18n } from '../../contexts/i18n';

import { RATING_KEY_MAP } from './rating-key-map';

import type { TranslationKey } from '@shared/constants/translations';
import type { Configs } from '@shared/types/configs';
import type { I18nService } from '@shared/types/i18n';
import type { Maybe } from '@shared/types/utils';

import STYLES from './ReviewRating.module.scss';

import { getAriaHidden } from '@shared/utils-frontend/a11y';
import { withConfiguration } from '@shared/utils-frontend/contexts/ConfigurationContext';
import { scoreSummaryForReview } from '@shared/utils-frontend/score';

export const SPACING_VARIANTS = {
  DEFAULT: 'default',
  NO_PADDING: 'no-padding',
  FLUSH: 'flush',
  FIT_CONTENT: 'fit-content',
  FIXED_RATIO: 'fixed-ratio',
} as const;

export type SpacingVariant =
  (typeof SPACING_VARIANTS)[keyof typeof SPACING_VARIANTS];

type Props = {
  i18n: I18nService;
  spacingVariant?: SpacingVariant;
  score: number;
  count?: Maybe<number>;
  image?: Maybe<string>;
  large?: boolean;
  index?: number;
  configs: Configs;
  scoreDescription?: string;
  hideTitle?: boolean;
  hideSubtitle?: boolean;
  onlyShowCount?: boolean;
};

const cls = cssModules(STYLES);

const ReviewRating = ({
  configs,
  count,
  hideSubtitle,
  hideTitle,
  i18n,
  image,
  index,
  large,
  onlyShowCount,
  score,
  scoreDescription,
  spacingVariant = SPACING_VARIANTS.DEFAULT,
}: Props) => {
  if (!score || !count) {
    return null;
  }
  const { enableReviewUIIntoCommon } = configs;

  const formatScore = i18n.formatNumber(score, 1);
  const formatCount = i18n.formatNumber(count, 0);
  const ratingKey = scoreSummaryForReview(score);

  let ariaLabel = '';
  let title = null;

  if (!image || enableReviewUIIntoCommon) {
    const titleText =
      scoreDescription ??
      (ratingKey && i18n.translate(RATING_KEY_MAP[ratingKey])) ??
      '';
    title = hideTitle ? null : titleText;

    ariaLabel = i18n.translate('aria_trip_com_review_rating', {
      desc: titleText,
      score,
      count,
    });
  } else {
    ariaLabel = i18n.translate('aria_trip_advisor_review_rating', {
      score,
      count,
    });
    title = hideTitle ? null : (
      <img
        src={image}
        alt={score.toString()}
        className={STYLES.ReviewRating__image}
      />
    );
  }

  const subtitle =
    count > 9
      ? i18n.translate('Reviews_label_X', {
          0: formatCount,
        })
      : i18n.translate(`Reviews_label_${count}` as TranslationKey);

  const subtitleText = onlyShowCount ? formatCount : subtitle;

  const variantClassName =
    spacingVariant !== SPACING_VARIANTS.DEFAULT
      ? `ReviewRating--${spacingVariant}`
      : null;

  return (
    <BpkRating
      className={cls('ReviewRating', variantClassName)}
      ariaLabel={ariaLabel}
      aria-hidden={getAriaHidden(index)}
      value={formatScore}
      title={title}
      subtitle={hideSubtitle ? undefined : subtitleText}
      showScale={enableReviewUIIntoCommon || !image}
      ratingScale={RATING_SCALES.zeroToFive}
      size={large ? RATING_SIZES.large : RATING_SIZES.base}
    />
  );
};

export default withI18n(withConfiguration(ReviewRating));
